import { useCallback, useState } from 'react';
import { motion, useAnimate } from 'framer-motion';
import { Tooltip } from '@tempmee/react';
import { OutlineHeartIcon, SolidHeartIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';

interface IFavoriteHeartButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isFavorite?: boolean;
  isPendingRemoval?: boolean;
}

// Keyframes matching mobile: rise+tilt-left → top-center → fall+tilt-right → rest
const heartBounceKeyframes = {
  y: [0, -6, -10, -6, 0],
  rotate: [0, -18, 0, 18, 0],
};
const heartBounceDuration = 0.38; // 100+80+100+100 = 380ms

export const FavoriteHeartButton = ({
  onClick,
  disabled = false,
  isFavorite = false,
  isPendingRemoval = false,
}: IFavoriteHeartButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [heartFilled, setHeartFilled] = useState(false);
  const [scope, animate] = useAnimate();

  const getHeartColor = () => {
    if (isFavorite || heartFilled) {
      return isHovered ? colors.favorites['350'] : colors.favorites['400'];
    }
    return colors.favorites['400'];
  };

  const getOutline = () => {
    if (isFavorite) return false;
    if (heartFilled) return false;
    return !isHovered;
  };

  const handleClick = useCallback(async () => {
    if (disabled) return;

    if (isFavorite) {
      onClick();
    } else {
      setHeartFilled(true);
      try {
        await animate('[data-testid="heart-bounce-wrapper"]', heartBounceKeyframes, {
          duration: heartBounceDuration,
        });
      } catch {
        // best-effort bounce; never block the save
      }
      onClick();
    }
  }, [disabled, isFavorite, animate, onClick]);

  const tooltipContent = isFavorite ? 'Remove' : 'Add to Favorites';

  return (
    // scope ref on the wrapper, not the button: Tooltip clones the button and overrides its ref
    <div
      ref={scope}
      style={{
        position: 'absolute',
        top: 10,
        right: 12,
      }}
    >
      <Tooltip
        title={tooltipContent}
        placement="bottom"
        disabled={disabled}
      >
        <button
          type="button"
          data-testid="favorite-heart-button"
          data-cy="favorite-button"
          disabled={disabled}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            all: 'unset',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.4 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            filter: isHovered ? 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))' : 'none',
            transition: 'filter 0.2s',
          }}
        >
          <motion.div data-testid="heart-bounce-wrapper">
            {isFavorite ? (
              <div style={{ position: 'relative' }}>
                <OutlineHeartIcon size={25} color={colors.favorites['400']} />
                <motion.div
                  data-testid="heart-fade-overlay"
                  animate={{ opacity: isPendingRemoval ? 0 : 1 }}
                  transition={{ duration: 0.3 }}
                  style={{ position: 'absolute', top: 0, left: 0 }}
                >
                  <SolidHeartIcon size={25} color={getHeartColor()} />
                </motion.div>
              </div>
            ) : (
              getOutline() ? (
                <OutlineHeartIcon size={25} color={getHeartColor()} />
              ) : (
                <SolidHeartIcon size={25} color={getHeartColor()} />
              )
            )}
          </motion.div>
        </button>
      </Tooltip>
    </div>
  );
};
